import axios, { type InternalAxiosRequestConfig } from "axios";
import { getAccessToken } from "@/lib/auth";
import { getStoredCurrentEnvironment } from "@/lib/environment";

const glopicApi = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL}/admin/glowpic`,
  headers: {
    "Content-Type": "application/json",
  },
});

const WRITE_METHODS = new Set(["post", "put", "patch", "delete"]);

type GlowpicRiskTarget = {
  operationKey: string;
  targetType: string;
  targetId: string;
};
const USER_RISK_OPERATION: Record<string, string> = {
  status: "status.write",
  "adjust-points": "points.write",
  "grant-vip": "vip.grant",
};

function requestBody(config: InternalAxiosRequestConfig): Record<string, unknown> {
  if (config.data && typeof config.data === "object") {
    return config.data as Record<string, unknown>;
  }
  if (typeof config.data === "string") {
    try {
      return JSON.parse(config.data) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return {};
}

function glowpicRiskTarget(config: InternalAxiosRequestConfig): GlowpicRiskTarget | null {
  if (!WRITE_METHODS.has((config.method || "").toLowerCase())) return null;
  const path = (config.url || "").split("?", 1)[0];
  const body = requestBody(config);
  if (path === "/config/validate") return null;
  if (path === "/config/rollback") {
    return {
      operationKey: "glowpic.config.rollback",
      targetType: "config_backup",
      targetId: String(body.backup_id || "unknown"),
    };
  }
  let match = path.match(/^\/config\/([^/]+)$/);
  if (match) {
    return {
      operationKey: "glowpic.config.write",
      targetType: "config",
      targetId: decodeURIComponent(match[1]),
    };
  }
  if (path === "/config") {
    return {
      operationKey: "glowpic.config.write",
      targetType: "config",
      targetId: String(body.config_key || "new"),
    };
  }
  match = path.match(/^\/users\/([^/]+)\/(status|adjust-points|grant-vip)$/);
  if (match) {
    const operation = USER_RISK_OPERATION[match[2]];
    if (!operation) return null;
    return {
      operationKey: `glowpic.user.${operation}`,
      targetType: "user",
      targetId: decodeURIComponent(match[1]),
    };
  }
  match = path.match(/^\/vip\/packages(?:\/([^/]+)(?:\/enabled)?)?$/);
  if (match) {
    return {
      operationKey: "glowpic.vip_package.write",
      targetType: "package",
      targetId: match[1]
        ? decodeURIComponent(match[1])
        : String(body.package_id || "new"),
    };
  }
  match = path.match(/^\/orders\/([^/]+)\/status$/);
  if (match) {
    return {
      operationKey: "glowpic.order.status.write",
      targetType: "order",
      targetId: decodeURIComponent(match[1]),
    };
  }
  return null;
}

async function createGlowpicRiskConfirmation(target: GlowpicRiskTarget): Promise<string> {
  const currentEnv = getStoredCurrentEnvironment();
  if (!currentEnv?.is_prod) return "";
  if (!window.confirm(`你正在 ${currentEnv.display_name} 执行高危操作，请确认继续。`)) {
    throw new Error("RISK_CONFIRMATION_CANCELLED");
  }
  const token = getAccessToken();
  const response = await axios.post(
    `${import.meta.env.VITE_API_URL}/quantum/business/risk/confirmations`,
    {
      env_id: currentEnv.id,
      operation_key: target.operationKey,
      target_type: target.targetType,
      target_id: target.targetId,
    },
    { headers: token ? { Authorization: `Bearer ${token}` } : undefined },
  );
  const confirmationToken = response.data?.data?.confirmation_token;
  if (typeof confirmationToken !== "string" || !confirmationToken) {
    throw new Error("RISK_CONFIRMATION_UNAVAILABLE");
  }
  return confirmationToken;
}

glopicApi.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  const currentEnv = getStoredCurrentEnvironment();
  if (currentEnv?.id && !config.headers["X-Admin-Env-Id"]) {
    config.headers["X-Admin-Env-Id"] = currentEnv.id;
  }
  return config;
});

glopicApi.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const target = glowpicRiskTarget(config);
  if (!target || config.headers["X-Risk-Confirmation-Token"]) return config;
  const confirmationToken = await createGlowpicRiskConfirmation(target);
  if (confirmationToken) {
    config.headers["X-Risk-Confirmation-Token"] = confirmationToken;
    config.headers["X-Risk-Target-Type"] = target.targetType;
    config.headers["X-Risk-Target-Id"] = target.targetId;
  }
  return config;
});

glopicApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export { glopicApi };
export default glopicApi;
